import React, { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { useAuth } from '../../context/AuthContext';
import { SearchContext } from '../Layout';

const navItems = [
  { label: 'Home', path: '/dashboard', icon: '⌂' },
  { label: 'Send', path: '/send-test', icon: '✈' },
  { label: 'Results', path: '/results', icon: '▣' },
  { label: 'Clients', path: '/clients', icon: '◉' },
];

const moreItems = [
  { label: 'Follow Ups', path: '/flagged-follow-ups', icon: '🚩' },
  { label: 'Earnings', path: '/earnings', icon: '₵' },
  { label: 'Profile', path: '/profile', icon: '☻' },
  { label: 'Security', path: '/change-password', icon: '♢' },
];

const getInitials = (user) => `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || 'VP';

const MobileLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);

  const pageTitle = useMemo(() => {
    const allItems = [...navItems, ...moreItems];
    return allItems.find(item => item.path === location.pathname)?.label || 'Portal';
  }, [location.pathname]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    const searchTarget = ['/dashboard', '/results', '/clients', '/flagged-follow-ups'].includes(location.pathname)
      ? location.pathname
      : '/results';
    navigate(`${searchTarget}?q=${encodeURIComponent(trimmed)}`);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setSheetOpen(false);
  };

  return (
    <Shell>
      <TopBar>
        <BrandCluster>
          <LogoMark><img src="/logo192.png" alt="Visioning" /></LogoMark>
          <div>
            <Eyebrow>Practitioner</Eyebrow>
            <Title>{pageTitle}</Title>
          </div>
        </BrandCluster>
        <AvatarButton type="button" onClick={() => setSheetOpen(true)} aria-label="Open profile menu">
          {getInitials(user)}
        </AvatarButton>
      </TopBar>

      <SearchForm onSubmit={handleSearchSubmit}>
        <SearchIcon>⌕</SearchIcon>
        <SearchInput
          type="search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search clients, results, dates…"
          aria-label="Search practitioner portal"
        />
      </SearchForm>

      <Content>
        <SearchContext.Provider value={{ searchQuery, setSearchQuery }}>
          {children}
        </SearchContext.Provider>
      </Content>

      <BottomNav aria-label="Mobile practitioner navigation">
        {navItems.map(item => (
          <BottomNavItem key={item.path} to={item.path}>
            <span>{item.icon}</span>
            <small>{item.label}</small>
          </BottomNavItem>
        ))}
        <MoreButton type="button" onClick={() => setSheetOpen(true)}>
          <span>•••</span>
          <small>More</small>
        </MoreButton>
      </BottomNav>

      <SheetBackdrop $open={sheetOpen} onClick={() => setSheetOpen(false)} />
      <MoreSheet $open={sheetOpen} role="dialog" aria-label="More portal actions">
        <Handle />
        <SheetProfile>
          <ProfileAvatar>{getInitials(user)}</ProfileAvatar>
          <div>
            <strong>{user?.firstName || 'Practitioner'} {user?.lastName || ''}</strong>
            <span>{user?.email || 'Mobile workspace'}</span>
          </div>
        </SheetProfile>
        <SheetGrid>
          {moreItems.map(item => (
            <SheetAction key={item.path} type="button" onClick={() => handleNavigate(item.path)}>
              <span>{item.icon}</span>
              {item.label}
            </SheetAction>
          ))}
        </SheetGrid>
        <LogoutButton type="button" onClick={logout}>Sign out</LogoutButton>
      </MoreSheet>
    </Shell>
  );
};

const Shell = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #eff7ff 0%, #f8fbff 42%, #ffffff 100%);
  color: #0f172a;
  padding: max(14px, env(safe-area-inset-top)) 14px calc(92px + env(safe-area-inset-bottom));
`;

const TopBar = styled.header`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const BrandCluster = styled.div`
  align-items: center;
  display: flex;
  gap: 11px;
  min-width: 0;
`;

const LogoMark = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #0369a1, #0ea5e9);
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(14, 165, 233, 0.24);
  display: flex;
  height: 46px;
  justify-content: center;
  overflow: hidden;
  width: 46px;

  img { height: 100%; object-fit: contain; width: 100%; }
`;

const Eyebrow = styled.div`
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  color: #0f172a;
  font-size: 1.12rem;
  line-height: 1.15;
  margin: 2px 0 0;
`;

const AvatarButton = styled.button`
  align-items: center;
  background: #0f2f57;
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(15, 47, 87, 0.22);
  color: white;
  display: flex;
  font-weight: 900;
  height: 44px;
  justify-content: center;
  width: 44px;
`;

const SearchForm = styled.form`
  align-items: center;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(191, 219, 254, 0.92);
  border-radius: 20px;
  box-shadow: 0 14px 32px rgba(30, 64, 175, 0.08);
  display: flex;
  gap: 8px;
  margin: 16px 0 14px;
  padding: 11px 14px;
`;

const SearchIcon = styled.span`
  color: #0284c7;
  font-size: 1.25rem;
  font-weight: 900;
`;

const SearchInput = styled.input`
  background: transparent;
  border: 0;
  color: #0f172a;
  flex: 1;
  font-size: 0.95rem;
  outline: none;
  min-width: 0;

  &::placeholder { color: #94a3b8; }
`;

const Content = styled.main`
  position: relative;
  z-index: 1;
`;

const BottomNav = styled.nav`
  align-items: center;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 26px;
  bottom: calc(10px + env(safe-area-inset-bottom));
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.16);
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(5, 1fr);
  left: 10px;
  padding: 8px;
  position: fixed;
  right: 10px;
  transition: opacity 180ms ease, transform 180ms ease;
  z-index: 60;

  body.follow-up-detail-open &,
  body.result-detail-open &,
  body.client-detail-open & {
    opacity: 0;
    pointer-events: none;
    transform: translateY(110%);
  }
`;

const navItemStyles = `
  align-items: center;
  border-radius: 18px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  font-size: 1.05rem;
  font-weight: 800;
  gap: 2px;
  justify-content: center;
  min-height: 54px;
  text-decoration: none;

  small { font-size: 0.65rem; font-weight: 900; }
`;

const BottomNavItem = styled(NavLink)`
  ${navItemStyles}

  &.active {
    background: linear-gradient(135deg, #e0f2fe, #dbeafe);
    color: #0369a1;
  }
`;

const MoreButton = styled.button`
  ${navItemStyles}
  background: transparent;
  border: 0;
`;

const SheetBackdrop = styled.div`
  background: rgba(15, 23, 42, 0.38);
  inset: 0;
  opacity: ${p => (p.$open ? 1 : 0)};
  pointer-events: ${p => (p.$open ? 'auto' : 'none')};
  position: fixed;
  transition: opacity 180ms ease;
  z-index: 70;
`;

const MoreSheet = styled.section`
  background: white;
  border-radius: 30px 30px 0 0;
  bottom: 0;
  box-shadow: 0 -18px 48px rgba(15, 23, 42, 0.22);
  left: 0;
  padding: 10px 16px calc(20px + env(safe-area-inset-bottom));
  position: fixed;
  right: 0;
  transform: translateY(${p => (p.$open ? '0' : '110%')});
  transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 80;
`;

const Handle = styled.div`
  background: #cbd5e1;
  border-radius: 99px;
  height: 5px;
  margin: 0 auto 16px;
  width: 46px;
`;

const SheetProfile = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #f8fafc, #eff6ff);
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  display: flex;
  gap: 12px;
  padding: 13px;

  strong { display: block; font-size: 0.95rem; }
  span { color: #64748b; display: block; font-size: 0.78rem; margin-top: 3px; }
`;

const ProfileAvatar = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #0369a1, #0ea5e9);
  border-radius: 18px;
  color: white;
  display: flex;
  font-weight: 900;
  height: 48px;
  justify-content: center;
  width: 48px;
`;

const SheetGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, 1fr);
  margin: 14px 0;
`;

const SheetAction = styled.button`
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  color: #0f172a;
  display: flex;
  font-weight: 850;
  gap: 9px;
  min-height: 58px;
  padding: 0 12px;
  text-align: left;

  span { font-size: 1.08rem; }
`;

const LogoutButton = styled.button`
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 18px;
  color: #be123c;
  font-weight: 900;
  min-height: 50px;
  width: 100%;
`;

export default MobileLayout;
